import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, ChevronDown, ChevronUp, CheckCircle, Circle, Plus, Trash2, FileText, RotateCcw } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import VideoUrlInput from '../../components/Common/VideoUrlInput';
import MicroQuizEditor from '../../components/Courses/MicroQuizEditor';

// ─── Step metadata ────────────────────────────────────────────────────────────
const STEPS = [
  { code: 'A', key: 'stepA_Why', label: 'A — Why', desc: 'Motivational hook: why this skill matters', icon: '🎯', color: 'bg-indigo-500', type: 'video+text' },
  { code: 'B', key: 'stepB_Story', label: 'B — Story', desc: 'Real-world narrative or case video', icon: '📖', color: 'bg-blue-500', type: 'video+text' },
  { code: 'C', key: 'stepC_Framework', label: 'C — Framework', desc: 'Core concept, diagram, key points', icon: '🧩', color: 'bg-cyan-600', type: 'rich-text' },
  { code: 'D', key: 'stepD_Practice', label: 'D — Practice', desc: 'Interactive activity or quiz', icon: '⚙️', color: 'bg-emerald-500', type: 'practice' },
  { code: 'E', key: 'stepE_FlashCard', label: 'E — Flash Cards', desc: 'Flip cards for reinforcement', icon: '🃏', color: 'bg-amber-500', type: 'flashcards' },
  { code: 'F', key: 'stepF_AdvancedPractice', label: 'F — Advanced Practice', desc: 'Deeper exercise / essay submission', icon: '🔬', color: 'bg-orange-500', type: 'reflection' },
  { code: 'G', key: 'stepH_Notes', label: 'G — Notes', desc: 'Optional personal notes space', icon: '📝', color: 'bg-gray-500', type: 'notes', optional: true },
];

// ─── Rich Text Area ───────────────────────────────────────────────────────────
const RichArea = ({ label, value, onChange, rows = 4, placeholder = '' }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
    <textarea rows={rows} value={value || ''} onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-400 focus:border-transparent resize-none" />
  </div>
);

// ─── FlashCard editor ─────────────────────────────────────────────────────────
const FlashCardEditor = ({ cards = [], onChange }) => {
  const add = () => onChange([...cards, { front: '', back: '', order: cards.length }]);
  const remove = (i) => onChange(cards.filter((_, idx) => idx !== i));
  const update = (i, field, val) => {
    const updated = [...cards];
    updated[i] = { ...updated[i], [field]: val };
    onChange(updated);
  };
  return (
    <div className="space-y-3">
      {cards.map((card, i) => (
        <div key={i} className="grid grid-cols-2 gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <div>
            <label className="text-xs font-bold text-amber-700 mb-1 block">FRONT (Question)</label>
            <textarea rows={2} value={card.front} onChange={e => update(i, 'front', e.target.value)}
              placeholder="Question or prompt…"
              className="w-full px-2 py-1.5 border border-amber-200 rounded-lg text-xs focus:ring-2 focus:ring-amber-300 resize-none" />
          </div>
          <div className="relative">
            <label className="text-xs font-bold text-amber-700 mb-1 block">BACK (Answer)</label>
            <textarea rows={2} value={card.back} onChange={e => update(i, 'back', e.target.value)}
              placeholder="Key insight or answer…"
              className="w-full px-2 py-1.5 border border-amber-200 rounded-lg text-xs focus:ring-2 focus:ring-amber-300 resize-none" />
            <button onClick={() => remove(i)} type="button"
              className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600">
              <Trash2 className="w-2.5 h-2.5" />
            </button>
          </div>
        </div>
      ))}
      <button type="button" onClick={add}
        className="w-full py-2 border-2 border-dashed border-amber-300 text-amber-600 rounded-xl text-sm font-semibold hover:bg-amber-50 flex items-center justify-center gap-2">
        <Plus className="w-4 h-4" /> Add Flash Card
      </button>
    </div>
  );
};

// ─── Reflection question editor ───────────────────────────────────────────────
const ReflectionEditor = ({ questions = [], onChange, label = 'Reflection Questions' }) => {
  const add = () => onChange([...questions, { question: '', type: 'essay', wordLimit: 200, isRequired: true, sequence: questions.length + 1 }]);
  const remove = (i) => onChange(questions.filter((_, idx) => idx !== i));
  const update = (i, field, val) => { const u = [...questions]; u[i] = { ...u[i], [field]: val }; onChange(u); };
  return (
    <div className="space-y-3">
      <label className="block text-sm font-semibold text-gray-700">{label}</label>
      {questions.map((q, i) => (
        <div key={i} className="bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-2">
          <div className="flex items-start gap-2">
            <span className="w-6 h-6 bg-indigo-600 text-white rounded-lg text-xs font-black flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
            <textarea rows={2} value={q.question} onChange={e => update(i, 'question', e.target.value)}
              placeholder="Write your question…"
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 resize-none" />
            <button type="button" onClick={() => remove(i)} className="text-red-400 hover:text-red-600 mt-1">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-3 ml-8">
            <select value={q.type} onChange={e => update(i, 'type', e.target.value)}
              className="text-xs px-2 py-1 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400">
              <option value="essay">Essay</option>
              <option value="text">Short text</option>
              <option value="file-upload">File upload</option>
            </select>
            <input type="number" value={q.wordLimit || 200} onChange={e => update(i, 'wordLimit', parseInt(e.target.value))}
              placeholder="Word limit" className="w-24 text-xs px-2 py-1 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400" />
          </div>
        </div>
      ))}
      <button type="button" onClick={add}
        className="w-full py-2 border-2 border-dashed border-indigo-300 text-indigo-600 rounded-xl text-sm font-semibold hover:bg-indigo-50 flex items-center justify-center gap-2">
        <Plus className="w-4 h-4" /> Add Question
      </button>
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const LearningFlowEditor = () => {
  const { id } = useParams(); // courseId
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [flow, setFlow] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openStep, setOpenStep] = useState('A');

  useEffect(() => { if (id) fetchCourse(); }, [id]);

  const fetchCourse = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/courses/${id}`);
      if (data.success) {
        setCourse(data.data);
        setFlow(data.data.learningFlow || {});
      }
    } catch (e) { toast.error('Failed to load course.'); }
    finally { setLoading(false); }
  };

  const updateStep = (stepKey, field, value) => {
    setFlow(prev => ({
      ...prev,
      [stepKey]: { ...(prev[stepKey] || {}), [field]: value }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const { data } = await api.put(`/courses/${id}`, { learningFlow: flow });
      if (data.success) {
        setFlow(data.data.learningFlow || flow);
        toast.success('Learning flow saved!');
      } else {
        toast.error(data.message || 'Save failed.');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed.');
    } finally { setSaving(false); }
  };

  const getStepCompletion = (stepKey) => {
    const s = flow[stepKey];
    if (!s) return false;
    if (stepKey === 'stepA_Why' || stepKey === 'stepB_Story') return !!(s.videoUrl || s.text || s.narrative);
    if (stepKey === 'stepC_Framework') return !!(s.content || s.keyPoints?.length);
    if (stepKey === 'stepD_Practice') {
      if (s.activityType === 'quiz') return !!(s.questions?.length);
      return !!(s.instructions);
    }
    if (stepKey === 'stepE_FlashCard') return !!(s.cards?.length);
    if (stepKey === 'stepF_AdvancedPractice') return !!(s.instructions || s.questions?.length);
    if (stepKey === 'stepH_Notes') return !!s.isEnabled;
    return true;
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
    </div>
  );

  const completedCount = STEPS.filter(s => getStepCompletion(s.key)).length;

  return (
    <div className="max-w-3xl mx-auto py-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(`/courses/${id}/builder`)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft className="w-4 h-4" /> Back to Builder
        </button>
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-800 text-white rounded-xl font-bold text-sm shadow-lg hover:shadow-xl transition-all disabled:opacity-60">
          <Save className="w-4 h-4" />
          {saving ? 'Saving…' : 'Save Flow'}
        </button>
      </div>

      {/* Course title + progress */}
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 rounded-2xl p-6 text-white shadow-xl">
        <p className="text-xs font-bold opacity-70 mb-1">LEARNING FLOW EDITOR</p>
        <h1 className="text-xl font-black">{course?.title}</h1>
        <p className="text-indigo-200 text-sm mt-1">{course?.courseNumber} · {course?.stage?.replace('_', ' ')}</p>
        <div className="mt-4 flex items-center gap-3">
          <div className="flex-1 h-2 bg-indigo-900/40 rounded-full">
            <div className="h-full bg-white/80 rounded-full transition-all" style={{ width: `${(completedCount / 7) * 100}%` }} />
          </div>
          <span className="text-sm font-bold">{completedCount}/7 steps</span>
        </div>
        {/* Step pills */}
        <div className="flex gap-1.5 mt-3 flex-wrap">
          {STEPS.map(s => (
            <button key={s.code} type="button"
              onClick={() => setOpenStep(s.code)}
              className={`w-8 h-8 rounded-lg text-xs font-black transition-all
                ${openStep === s.code ? 'bg-white text-indigo-700 shadow-lg scale-110' : getStepCompletion(s.key) ? 'bg-white/30 text-white' : 'bg-white/10 text-white/60'}`}>
              {s.code}
            </button>
          ))}
        </div>
      </div>

      {/* Step accordion */}
      <div className="space-y-3">
        {STEPS.map(step => {
          const isOpen = openStep === step.code;
          const done = getStepCompletion(step.key);
          const stepData = flow[step.key] || {};

          return (
            <div key={step.code} className={`bg-white rounded-2xl border-2 shadow-sm overflow-hidden transition-all
              ${isOpen ? 'border-indigo-300 shadow-md' : done ? 'border-green-200' : 'border-gray-100'}`}>
              {/* Step header */}
              <button type="button" onClick={() => setOpenStep(isOpen ? null : step.code)}
                className="w-full flex items-center gap-4 p-4 text-left hover:bg-gray-50">
                <div className={`w-10 h-10 ${step.color} rounded-xl flex items-center justify-center text-xl flex-shrink-0 shadow-md`}>
                  {step.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-gray-900">{step.label}</p>
                    {step.optional && <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full font-bold">OPTIONAL</span>}
                    {done && <CheckCircle className="w-4 h-4 text-green-500" />}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{step.desc}</p>
                </div>
                {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
              </button>

              {/* Step content */}
              {isOpen && (
                <div className="px-5 pb-5 pt-1 space-y-4 border-t border-gray-100">

                  {/* A — Why */}
                  {step.code === 'A' && (
                    <>
                      <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Step Title</label>
                        <input type="text" value={stepData.title || ''} onChange={e => updateStep(step.key, 'title', e.target.value)}
                          placeholder="e.g. Why this skill is essential"
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-400" />
                      </div>
                      <VideoUrlInput label="Why Video URL" value={stepData.videoUrl} onChange={v => updateStep(step.key, 'videoUrl', v)} focusRingClass="focus:ring-indigo-400" />
                      <RichArea label="Supporting Text" value={stepData.text} onChange={v => updateStep(step.key, 'text', v)} placeholder="Write the 'why' context — what problem does this skill solve?" />
                    </>
                  )}

                  {/* B — Story */}
                  {step.code === 'B' && (
                    <>
                      <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Step Title</label>
                        <input type="text" value={stepData.title || ''} onChange={e => updateStep(step.key, 'title', e.target.value)}
                          placeholder="e.g. The Story of Transformation"
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-400" />
                      </div>
                      <VideoUrlInput label="Story Video URL" value={stepData.videoUrl} onChange={v => updateStep(step.key, 'videoUrl', v)} focusRingClass="focus:ring-indigo-400" />
                      <RichArea label="Written Narrative" value={stepData.narrative} onChange={v => updateStep(step.key, 'narrative', v)} placeholder="Tell a real-world story that illustrates this skill…" rows={5} />
                    </>
                  )}

                  {/* C — Framework */}
                  {step.code === 'C' && (
                    <>
                      <RichArea label="Framework / Core Concept" value={stepData.content} onChange={v => updateStep(step.key, 'content', v)} placeholder="Explain the framework, model, or core concept…" rows={6} />
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Key Points</label>
                        {(stepData.keyPoints || []).map((pt, i) => (
                          <div key={i} className="flex gap-2 mb-2">
                            <input type="text" value={pt} onChange={e => {
                              const kp = [...(stepData.keyPoints || [])]; kp[i] = e.target.value;
                              updateStep(step.key, 'keyPoints', kp);
                            }} placeholder={`Key point ${i + 1}`}
                              className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-400" />
                            <button type="button" onClick={() => {
                              const kp = (stepData.keyPoints || []).filter((_, idx) => idx !== i);
                              updateStep(step.key, 'keyPoints', kp);
                            }} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        ))}
                        <button type="button" onClick={() => updateStep(step.key, 'keyPoints', [...(stepData.keyPoints || []), ''])}
                          className="text-sm text-indigo-600 font-semibold flex items-center gap-1 hover:text-indigo-800">
                          <Plus className="w-4 h-4" /> Add key point
                        </button>
                      </div>
                    </>
                  )}

                  {/* D — Practice */}
                  {step.code === 'D' && (
                    <>
                      <RichArea label="Instructions" value={stepData.instructions} onChange={v => updateStep(step.key, 'instructions', v)} placeholder="What should the student do in this practice activity?" />
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Activity Type</label>
                        <div className="flex gap-2 flex-wrap">
                          {['reflection', 'quiz', 'discussion', 'simulation'].map(t => (
                            <button key={t} type="button" onClick={() => updateStep(step.key, 'activityType', t)}
                              className={`px-3 py-1.5 rounded-xl text-sm font-bold capitalize border-2 transition-all
                                ${stepData.activityType === t ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-600 hover:border-emerald-300'}`}>
                              {t}
                            </button>
                          ))}
                        </div>
                      </div>
                      {stepData.activityType === 'quiz' && (
                        <MicroQuizEditor
                          questions={stepData.questions || []}
                          onChange={(q) => updateStep(step.key, 'questions', q)}
                          randomizeQuestions={stepData.randomizeQuestions !== false}
                          onRandomizeChange={(v) => updateStep(step.key, 'randomizeQuestions', v)}
                        />
                      )}
                    </>
                  )}

                  {/* E — Flash Cards */}
                  {step.code === 'E' && (
                    <FlashCardEditor cards={stepData.cards || []} onChange={cards => updateStep(step.key, 'cards', cards)} />
                  )}

                  {/* F — Advanced Practice */}
                  {step.code === 'F' && (
                    <>
                      <RichArea label="Instructions" value={stepData.instructions} onChange={v => updateStep(step.key, 'instructions', v)} placeholder="Advanced exercise instructions…" />
                      <ReflectionEditor questions={stepData.questions || []} onChange={q => updateStep(step.key, 'questions', q)} label="Submission Questions" />
                    </>
                  )}

                  {/* G — Notes */}
                  {step.code === 'G' && (
                    <>
                      <div className="flex items-center gap-3">
                        <label className="text-sm font-semibold text-gray-700">Enable Notes Step</label>
                        <button type="button" onClick={() => updateStep(step.key, 'isEnabled', !stepData.isEnabled)}
                          className={`w-12 h-6 rounded-full transition-all ${stepData.isEnabled ? 'bg-indigo-600' : 'bg-gray-200'}`}>
                          <div className={`w-5 h-5 bg-white rounded-full shadow transition-all mx-0.5 ${stepData.isEnabled ? 'translate-x-6' : 'translate-x-0'}`} />
                        </button>
                      </div>
                      {stepData.isEnabled && (
                        <RichArea label="Notes Prompt" value={stepData.prompt} onChange={v => updateStep(step.key, 'prompt', v)} placeholder="Write your personal notes and key takeaways here." />
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom save */}
      <button onClick={handleSave} disabled={saving}
        className="w-full py-4 bg-gradient-to-r from-indigo-600 to-indigo-800 text-white rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all disabled:opacity-60">
        {saving ? 'Saving…' : '💾 Save Learning Flow'}
      </button>
    </div>
  );
};

export default LearningFlowEditor;
